
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

export enum AppState {
  IDLE,
  LOADING,
  SUCCESS,
  ERROR,
}

export enum AppStage {
  PROMPTING = 'Prompt Conception',
  LOADING = 'Generating Video',
  EDITING = 'AI Image Editor',
  RESULT = 'Video Result',
}

export enum VeoModel {
  VEO_FAST = 'veo-3.1-fast-generate-preview',
  VEO = 'veo-3.1-generate-preview',
  VEO_3 = 'veo-3.0-generate-preview',
}

export enum AspectRatio {
  LANDSCAPE = '16:9',
  PORTRAIT = '9:16',
}

export enum Resolution {
  P720 = '720p',
  P1080 = '1080p',
}

export enum GenerationMode {
  TEXT_TO_VIDEO = 'Text to Video',
  FRAMES_TO_VIDEO = 'Frames to Video',
  REFERENCES_TO_VIDEO = 'References to Video',
  EXTEND_VIDEO = 'Extend Video',
}

export interface ImageFile {
  file: File;
  base64: string;
}

export interface VideoFile {
  file?: File;
  base64: string;
}

export interface PromptSequence {
  // === NEW: Scoping context ===
  id: string;                         // Unique sequence ID
  projectId?: string;                 // Optional project grouping
  dogmaId: string | null;             // The dogma bound to this sequence at creation

  // === Existing fields ===
  mainPrompt: string;
  extensionPrompts: string[];

  // === NEW: Dirty tracking ===
  status: PromptSequenceStatus;
  dirtyExtensions: number[];          // Indices of extensions needing regeneration

  // === NEW: Audit timestamps ===
  createdAt: string;
  rootModifiedAt?: string;            // Track when root was last modified
}

export enum PromptSequenceStatus {
  CLEAN = 'clean',
  ROOT_MODIFIED = 'root_modified',     // Root changed, extensions invalid
  EXTENSIONS_DIRTY = 'extensions_dirty', // Some extensions need regen
  GENERATING = 'generating',
}

export interface SequenceProgress {
  current: number;
  total: number;
}

// A serializable representation of an image for storing Dogmas in localStorage.
export interface DogmaImage {
  name: string;
  type: string;
  base64: string;
}

export interface Dogma {
  id: string;
  title: string;
  text: string;
  referenceImages: DogmaImage[];
  folderId?: string;
}



export interface CharacterImage extends DogmaImage {
  label: string; // e.g., 'Front View', 'Profile View'
  generatedBy?: string; // Model ID used for generation (e.g., 'flux_pro_v1.1')
}

export interface Character {
  id: string;
  name: string;
  description: string;
  images: CharacterImage[];
  voiceName?: string;
  sampleText?: string;
  folderId?: string;
}


export interface GenerateVideoParams {
  prompt: string;
  model: string; // Dynamic ID
  aspectRatio: AspectRatio;
  resolution: Resolution;
  mode: GenerationMode;
  startFrame?: ImageFile | null;
  endFrame?: ImageFile | null;
  referenceImages?: ImageFile[];
  styleImage?: ImageFile | null;
  inputVideo?: VideoFile | null;
  inputVideoObject?: any | null; // Changed from Video to any
  isLooping?: boolean;
  provider?: VideoProvider;
  vertexConfig?: VertexConfig;
  ltxParams?: LTXParams;
  withAudio?: boolean; // true = generate with audio, false = silent video
  ltxApiKey?: string;
  duration?: number;
  fps?: number;
  dogma?: Dogma;
  /**
   * Start Frame Mode - Controls how reference image is used for first frame
   * - prompt_only: No image sent (pure text-to-video)
   * - reference_soft: Image as soft reference (default Veo behavior)
   * - lock_first_frame: GUARANTEE first frame matches Nano Pro image
   */
  startFrameMode?: 'prompt_only' | 'reference_soft' | 'lock_first_frame';
}

export interface LTXParams {
  // === CORE GENERATION ===
  guidance_scale: number;
  num_inference_steps: number;
  seed?: number;
  negative_prompt?: string;
  mode: 't2v' | 'i2v' | 'v2v' | 'keyframe' | 'a2v'; // a2v = audio-to-video

  // === MODEL & QUALITY ===
  model: 'ltx-2-fast' | 'ltx-2-pro' | 'ltx-2-ultra';
  quality_preset: 'fast' | 'balanced' | 'pro' | 'cinema';

  // === DURATION & OUTPUT ===
  duration_sec: number;           // 5-20 seconds
  resolution: '720p' | '1080p' | '1440p' | '4k';
  fps: 24 | 25 | 30 | 50;
  aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:3';

  // === AUDIO (LTX-2 Exclusive) ===
  generate_audio: boolean;
  audio_prompt?: string;          // Specific audio description
  audio_file?: string;            // Base64 audio for audio-to-video mode
  audio_file_url?: string;        // URL to audio file (alternative to base64)

  // === CAMERA CONTROL ===
  camera_motion?: LTXCameraMotion;

  // === CONTROL MODELS (IC-LoRA) ===
  control_type?: 'none' | 'canny' | 'depth' | 'pose';
  control_image?: string;         // Base64 control guide image
  control_strength?: number;      // 0.0-1.0

  // === MULTI-KEYFRAME ===
  keyframes?: LTXKeyframe[];

  // === PROMPT ENHANCEMENT ===
  enhance_prompt?: boolean;

  // === RETAKE MODE ===
  retake_source_video?: string;   // Base64 or URL of video to retake
  retake_preserve?: ('structure' | 'motion' | 'colors' | 'audio')[];
}

export type LTXCameraMotion =
  | 'static'
  | 'pan_left' | 'pan_right'
  | 'tilt_up' | 'tilt_down'
  | 'dolly_in' | 'dolly_out'
  | 'track_left' | 'track_right'
  | 'crane_up' | 'crane_down'
  | 'arc_left' | 'arc_right'
  | 'handheld'
  | 'orbit'
  | 'zoom_in' | 'zoom_out';

export interface LTXKeyframe {
  image: string;                  // Base64
  timestamp_sec: number;          // Position in the video
  weight?: number;                // 0.0-1.0, influence strength
}

export interface LTXModel {
  id: 'ltx-2-fast' | 'ltx-2-pro' | 'ltx-2-ultra';
  name: string;
  description: string;
  maxDuration: number;
  maxResolution: string;
  estimatedTime: string;
  supportsAudio: boolean;
}

export interface RegeneratePromptParams {
  index: number;
  promptToRevise: string;
  instruction: string;
  visualContextBase64?: string;
}

export interface ReviseFollowingPromptsParams {
  dogma: Dogma | null;
  promptBefore: string | undefined;
  editedPrompt: string;
  promptsToRevise: string[];
}

export interface SavedShot {
  id: string;
  title?: string;
  prompt: string;
  thumbnail: string; // base64 string
  createdAt: string; // ISO date string
  model: string;
  aspectRatio: AspectRatio;
  resolution: Resolution;
  mode: GenerationMode;
  // P1: Video restoration fields
  videoUrl?: string; // Original URL
  previewImageBase64?: string; // Full keyframe image (not just thumbnail)

  // Advanced Storage
  videoStorage?: {
    type: 'google-drive' | 'local-download' | 'supabase';
    reference: string; // fileId or fileName
    url?: string;     // Public link if applicable
  };

  // === P1.4: REGENERATION METADATA ===
  /** Original root prompt before AI expansion */
  rootPrompt?: string;
  /** Final prompt after AI processing */
  finalPrompt?: string;
  /** Full shot specification from assistant */
  shotSpec?: {
    id?: string;
    label?: string;
    prompt?: string;
    cameraMove?: string;
    duration?: string | number;
    notes?: string;
    visualStyle?: string;
  };
  /** Video engine used (veo, ltx, wan, etc.) */
  engine?: string;
  /** Generation parameters for reproducibility */
  generationParams?: {
    seed?: number;
    fps?: number;
    duration?: number;
    startFrameMode?: 'prompt_only' | 'reference_soft' | 'lock_first_frame';
    guidanceScale?: number;
    negativePrompt?: string;
  };
  /** Reference images (base64) used for generation */
  referenceImages?: Array<{
    base64: string;
    mimeType: string;
    label: string;
    role: 'start_frame' | 'style' | 'character' | 'composition';
  }>;
  /** Character IDs referenced */
  characterIds?: string[];
  /** Dogma ID used */
  dogmaId?: string;
  /** Tags for organization */
  tags?: string[];
}

export interface Keyframe {
  timestamp: string;
  description: string;
  imageBase64: string;
}

export interface Storyboard {
  prompt: string;
  keyframes: Keyframe[];
}

export interface SequenceVideoData {
  url: string;
  prompt: string;
  isExtension: boolean;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
  thumbnail?: string; // base64
  video?: any; // Video object reference
  blob?: Blob; // Video blob for processing
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  image?: ImageFile | null;
}

export interface ComplianceResult {
  score: number;
  critique: string;
  revisedPrompt?: string;
}

// --- VERTEX AI EXTENSIONS ---
export enum VideoProvider {
  GEMINI = 'Gemini API',
  VERTEX = 'Google Veo',
  LTX = 'LTX-2 (Fal)',
  KLING = 'Kling 2.6 (Fal)',
}

export interface VertexConfig {
  projectId: string;
  location: string;
  accessToken: string;
}

// === NANO BANANA PRO: Storyboard & Shot Variants ===

export type StoryboardPreviewOwner = 'root' | 'extension' | 'character';

export interface StoryboardPreview {
  id: string;
  owner: StoryboardPreviewOwner;
  segmentIndex?: number;       // 0=root, 1..n=extensions (if owner != 'character')
  characterId?: string;
  baseImage?: ImageFile;       // Source image (assistantImage / lastFrame / char image)
  previewImage: ImageFile;     // Validated preview (Nano output)
  previewPrompt: string;       // Exact prompt corresponding to previewImage
  cameraNotes?: string;        // e.g., "35mm, plan taille, 3/4 face, caméra basse"
  movementNotes?: string;      // e.g., "dolly-in lent, pan léger"
  createdAt: string;
  updatedAt: string;
}

export interface ShotVariant {
  label: string;               // "Plan moyen", "Plan épaule", etc.
  previewImage: ImageFile;
  cameraNotes: string;
  deltaInstruction: string;    // Instruction used to obtain this variant
}

// Standard shot labels for "Couverture de plans"
export const STANDARD_SHOT_LIST = [
  'Plan d\'ensemble',
  'Demi-ensemble',
  'Plan moyen',
  'Plan genoux',
  'Plan américain',
  'Plan taille',
  'Plan poitrine',
  'Plan épaule',
  'Gros plan',
  'Très gros plan',
  'Plongée',
  'Contre-plongée',
] as const;

/**
 * Payload returned by AIEditorModal.onApply callback
 * Used for Nano Banana Pro prompt alignment
 */
export interface NanoApplyPayload {
  target: 'root' | 'extension' | 'character';
  segmentIndex: number | null;  // null = character edit, 0 = root, 1..N = extensions
  previewPrompt: string;
  previewImage: ImageFile;
  cameraNotes?: string;
  movementNotes?: string;
}

/**
 * Context for opening Nano editor from any entry point
 * Centralized in Studio.tsx via openNanoEditor()
 */
export interface NanoEditorContext {
  segmentIndex: number | null;
  target: 'root' | 'extension' | 'character';
  dogma: Dogma | null;
  baseImage?: ImageFile;
  initialPrompt?: string;
  // Master Shot UX
  mode?: 'default' | 'decoupage-master-shot';
  onApply?: (result: NanoApplyPayload) => void;
}

// === PERSISTENCE & USER ===

export interface UserProfile {
  id: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  api_key?: string; // Stored preference
  preferences?: {
    videoStorage?: 'google-drive' | 'local-download';
    [key: string]: any; // Allow other future preferences
  };
}

export interface ProjectState {
  promptSequence: PromptSequence | null;
  storyboardByIndex: Record<number, StoryboardPreview>;
  sequenceVideoData: Record<number, SequenceVideoData>;
  sequenceHistory: any[]; // Using any[] for now to match Studio.tsx usage, ideally typed
  assistants: {
    motionDescription: string | null;
    assistantExtensionContext: ImageFile | null;
    assistantImage: ImageFile | null;
    assistantReferenceVideo: VideoFile | null;
    mentionedCharacters: Character[];
    // P0: Include chat messages for full persistence
    messages?: ChatMessage[];
  };
  activeDogma: Dogma | null;
}

export interface Project {
  id: string;
  title: string;
  user_id: string;
  content_json?: ProjectState;
  updated_at: string;
  created_at: string;
}
